import { useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import { Table as TableComponent } from "@tanstack/react-table";
import Button from "@/components/Button";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { StudentRow } from "../types";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useSearchWithParams, useParams as useParamsHook } from "@/hooks/useSearchParams";
import MultiSelect from "@/components/MultiSelect";
import pluralize from "@/lib/pluralize";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import useModal from "@/hooks/useModal";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import { buildColumns, mapGraduationRequestsFromUniversities, isEligibleForGraduation } from "./_utils";
import { getStatusTagColor } from "../../_utils";
import { buildProgramLevelColumns, buildUniversityStudentDetailLink } from "../../../_shared/_utils";
import { APPROVE_GRADUATION_REQUESTS_MUTATION, REJECT_GRADUATION_REQUESTS_MUTATION } from "./mutations";
import { GET_PENDING_GRADUATION_REQUESTS_QUERY } from "./queries";
import { PendingGraduationRequestsQuery } from "./types";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import useCapabilities from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import {
  FILTER_PROGRAM_LEVEL_OPTIONS,
  PAT_STATUS_OPTIONS,
  convertPATStatusToUppercase
} from "../../../_shared/constants";
import { parseProgramLevelFilterSelections } from "../../../_shared/programLevelFilter";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import { useUniversityEmailPreview } from "../../../_shared/hooks/useUniversityEmailPreview";
import {
  UNIVERSITY_GRADUATION_CONGRATULATIONS_EMAIL_PREVIEW_QUERY,
  UNIVERSITY_GRADUATIONS_FINALIZED_EMAIL_PREVIEW_QUERY,
  UNIVERSITY_GRADUATIONS_REMOVED_EMAIL_PREVIEW_QUERY
} from "../../../_shared/queries/emailPreviewQueries";
import { useUniversityEmailPreviewForStaffCopy } from "../../../_shared/hooks/useUniversityEmailPreviewForStaffCopy";

export default function GraduationEligibleStudentsPage() {
  const { universityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll } = useBulkSelection();
  const rowSelectionRef = useRef<typeof rowSelection>({});
  const pendingRemoveRequestIdsRef = useRef<string[]>([]);
  const { isCompactLayout } = useIsCompactLayout();
  const { Menu, setMenu } = useTableRowMenu();
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParamsHook();
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(
    params.programs ? (Array.isArray(params.programs) ? params.programs : [params.programs]) : []
  );
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });
  const [selectedPatStatus, setSelectedPatStatus] = useState<string[]>(
    params.status
      ? Array.isArray(params.status)
        ? params.status.map((s) => convertPATStatusToUppercase(s))
        : [convertPATStatusToUppercase(params.status)]
      : []
  );
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [isGraduationDialogOpen, setIsGraduationDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [pendingRemoveRequestIds, setPendingRemoveRequestIds] = useState<string[]>([]);
  const [isFinalizeStaffPreviewOpen, setIsFinalizeStaffPreviewOpen] = useState(false);
  const [isFinalizeStudentPreviewOpen, setIsFinalizeStudentPreviewOpen] = useState(false);
  const [isRemovePreviewOpen, setIsRemovePreviewOpen] = useState(false);
  const forUniversityStaff = useUniversityEmailPreviewForStaffCopy();
  const {
    loadPreview: loadGraduationsFinalizedPreview,
    resetPreview: resetGraduationsFinalizedPreview,
    preview: graduationsFinalizedPreview,
    loading: graduationsFinalizedPreviewLoading,
    error: graduationsFinalizedPreviewError
  } = useUniversityEmailPreview(
    UNIVERSITY_GRADUATIONS_FINALIZED_EMAIL_PREVIEW_QUERY,
    "universityGraduationsFinalizedEmailPreview"
  );
  const {
    loadPreview: loadGraduationCongratulationsPreview,
    resetPreview: resetGraduationCongratulationsPreview,
    preview: graduationCongratulationsPreview,
    loading: graduationCongratulationsPreviewLoading,
    error: graduationCongratulationsPreviewError
  } = useUniversityEmailPreview(
    UNIVERSITY_GRADUATION_CONGRATULATIONS_EMAIL_PREVIEW_QUERY,
    "universityGraduationCongratulationsEmailPreview"
  );
  const {
    loadPreview: loadGraduationsRemovedPreview,
    resetPreview: resetGraduationsRemovedPreview,
    preview: graduationsRemovedPreview,
    loading: graduationsRemovedPreviewLoading,
    error: graduationsRemovedPreviewError
  } = useUniversityEmailPreview(
    UNIVERSITY_GRADUATIONS_REMOVED_EMAIL_PREVIEW_QUERY,
    "universityGraduationsRemovedEmailPreview"
  );
  const { Modal } = useModal();
  const universityStaffPermissions = useUniversityStaffPermissions();
  const { hasRole } = useCapabilities();
  const canFinalizeGraduations = hasRole(ROLE.EDUCATION_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN);
  const [error, setError] = useState<string | null>(null);

  // Keep rowSelectionRef in sync with rowSelection
  rowSelectionRef.current = rowSelection;

  const { data, loading, refetch } = useQuery<PendingGraduationRequestsQuery>(GET_PENDING_GRADUATION_REQUESTS_QUERY, {
    variables: { legacyId: universityId }
    // ...paginationVariables
  });

  const allStudents = useMemo(() => mapGraduationRequestsFromUniversities(data || {}), [data]);

  const selectedStudents = useMemo(() => {
    if (!isSelectedOnly) return [];
    const selectedIds = Object.keys(rowSelection);
    return allStudents.filter((student) => selectedIds.includes(student.id));
  }, [allStudents, rowSelection, isSelectedOnly]);

  const filteredStudents = useMemo(() => {
    const studentsToFilter = isSelectedOnly ? selectedStudents : allStudents;

    return studentsToFilter.filter((student) => {
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const matchesSearch =
          student.name?.toLowerCase().includes(searchLower) ||
          student.pgaId?.toLowerCase().includes(searchLower) ||
          student.email?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (selectedPrograms.length > 0) {
        const { curriculumVersions, levels } = parseProgramLevelFilterSelections(selectedPrograms);
        const matchesProgram =
          (!curriculumVersions || curriculumVersions.includes(student.program?.version)) &&
          (!levels || levels.includes(Number(student.program?.level)));
        if (!matchesProgram) return false;
      }

      if (selectedPatStatus.length > 0) {
        const studentPatStatusUppercase = convertPATStatusToUppercase(student.patStatus);
        const matchesPatStatus = selectedPatStatus.some((status) => status === studentPatStatusUppercase);
        if (!matchesPatStatus) return false;
      }

      return true;
    });
  }, [isSelectedOnly, selectedStudents, allStudents, debouncedSearch, selectedPrograms, selectedPatStatus]);

  const sortedStudents = useMemo(() => {
    return [...filteredStudents].sort((studentA, studentB) => {
      const studentAEligible = isEligibleForGraduation(studentA);
      const studentBEligible = isEligibleForGraduation(studentB);

      // Sort ineligible students to the top
      if (!studentAEligible && studentBEligible) return -1;
      if (studentAEligible && !studentBEligible) return 1;

      // Maintain original order for students with same eligibility status
      return 0;
    });
  }, [filteredStudents]);

  const students = sortedStudents;

  const eligibilityStats = useMemo(() => {
    const total = students.length;
    const eligible = students.filter(isEligibleForGraduation).length;
    const ineligible = total - eligible;

    return { total, eligible, ineligible };
  }, [students]);

  const handleViewStudent = useCallback((student: StudentRow) => {
    setSelectedStudent(student);
    setIsDetailsPanelOpen(true);
  }, []);

  const [approveGraduationRequests] = useMutation(APPROVE_GRADUATION_REQUESTS_MUTATION);
  const [rejectGraduationRequests] = useMutation(REJECT_GRADUATION_REQUESTS_MUTATION);

  const resolveGraduationRequestIds = useCallback(
    (graduationRequestId?: string) => {
      if (graduationRequestId) {
        return (
          data?.universities?.[0]?.graduationRequests
            ?.filter((req) => req.id === graduationRequestId)
            ?.map((req) => req.id) || []
        );
      }

      const currentRowSelection = Object.keys(rowSelectionRef.current).filter(Boolean);
      return currentRowSelection;
    },
    [data?.universities]
  );

  const openRemoveDialog = useCallback(
    (graduationRequestId?: string) => {
      const graduationRequestIds = resolveGraduationRequestIds(graduationRequestId);

      if (graduationRequestIds.length === 0) {
        setError("No graduation requests found to remove");
        return;
      }

      resetGraduationsRemovedPreview();
      pendingRemoveRequestIdsRef.current = graduationRequestIds;
      setPendingRemoveRequestIds(graduationRequestIds);
      setIsRemovePreviewOpen(false);
      setIsRemoveDialogOpen(true);
    },
    [resolveGraduationRequestIds, resetGraduationsRemovedPreview]
  );

  const handleRemoveFromEligible = useCallback(
    async (graduationRequestId?: string) => {
      openRemoveDialog(graduationRequestId);
    },
    [openRemoveDialog]
  );

  const confirmRemoveFromEligible = useCallback(async () => {
    try {
      const graduationRequestIds = pendingRemoveRequestIdsRef.current;

      if (graduationRequestIds.length === 0) {
        setError("No graduation requests found to remove");
        return;
      }

      const { data: mutationData } = await rejectGraduationRequests({
        variables: { input: { graduationRequestIds } }
      });

      if (mutationData?.rejectGraduationRequests?.success) {
        const selectedCount = graduationRequestIds.length;
        showSnackbar(`${selectedCount} ${pluralize(selectedCount, "student")} moved back to Current Students`);
        setIsRemoveDialogOpen(false);
        setIsRemovePreviewOpen(false);
        resetGraduationsRemovedPreview();
        pendingRemoveRequestIdsRef.current = [];
        setPendingRemoveRequestIds([]);
        deselectAll();
        refetch();
      } else {
        setError(mutationData?.rejectGraduationRequests?.message || "Error removing students");
      }
    } catch (error) {
      console.error("Error removing students from eligible:", error);
      setError("Error removing students");
    }
  }, [rejectGraduationRequests, refetch, deselectAll, resetGraduationsRemovedPreview]);

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        hasCheckbox: true,
        navigate,
        location,
        searchParamsUrl,
        universityId: universityId || "",
        handleViewStudent,
        setMenu,
        actionId,
        handleRemoveFromEligible
      }),
    [
      isCompactLayout,
      navigate,
      location,
      searchParamsUrl,
      universityId,
      handleViewStudent,
      setMenu,
      handleRemoveFromEligible
    ]
  );

  const programColumns = buildProgramLevelColumns(getStatusTagColor);

  const handleMarkEligibleForGraduation = async () => {
    try {
      const selectedRequestIds = Object.keys(rowSelectionRef.current).filter(Boolean);

      let graduationRequestIds: string[];

      if (selectedRequestIds.length === 0) {
        graduationRequestIds = allStudents.map((student) => student.id);
      } else {
        graduationRequestIds = selectedRequestIds;
      }

      if (graduationRequestIds.length === 0) {
        setError("Please select at least one student");
        return;
      }

      const { data: mutationData } = await approveGraduationRequests({
        variables: { input: { graduationRequestIds } }
      });

      if (mutationData?.approveGraduationRequests?.success) {
        const count = graduationRequestIds.length;
        showSnackbar(`${count} ${pluralize(count, "student")} graduated`);
        handleCloseGraduationDialog();
        deselectAll();
        refetch();
      } else {
        setError(mutationData?.approveGraduationRequests?.message || "Error approving graduation requests");
      }
    } catch (error) {
      console.error("Error approving graduation requests:", error);
      setError("Error approving graduation requests");
    }
  };

  const handleCloseGraduationDialog = () => {
    setIsGraduationDialogOpen(false);
    setIsFinalizeStaffPreviewOpen(false);
    setIsFinalizeStudentPreviewOpen(false);
    resetGraduationsFinalizedPreview();
    resetGraduationCongratulationsPreview();
    setError(null);
  };

  const handleCloseRemoveDialog = () => {
    setIsRemoveDialogOpen(false);
    setIsRemovePreviewOpen(false);
    resetGraduationsRemovedPreview();
    pendingRemoveRequestIdsRef.current = [];
    setPendingRemoveRequestIds([]);
    setError(null);
  };

  const getFinalizeGraduationRequestIds = useCallback(() => {
    const selectedRequestIds = Object.keys(rowSelectionRef.current).filter(Boolean);
    if (selectedRequestIds.length === 0) {
      return allStudents.map((student) => student.id);
    }

    return selectedRequestIds;
  }, [allStudents]);

  const finalizeGraduationRequestIds = getFinalizeGraduationRequestIds();
  const canPreviewFinalizeStaffEmail = finalizeGraduationRequestIds.length > 0;
  const canPreviewFinalizeStudentEmail = finalizeGraduationRequestIds.length === 1;

  const handlePreviewFinalizeStaffEmail = () => {
    const graduationRequestIds = getFinalizeGraduationRequestIds();
    if (graduationRequestIds.length === 0) {
      return;
    }

    setIsFinalizeStaffPreviewOpen(true);
    void loadGraduationsFinalizedPreview({
      graduationRequestIds,
      forUniversityStaff
    });
  };

  const handlePreviewFinalizeStudentEmail = () => {
    const graduationRequestIds = getFinalizeGraduationRequestIds();
    if (graduationRequestIds.length !== 1) {
      return;
    }

    setIsFinalizeStudentPreviewOpen(true);
    void loadGraduationCongratulationsPreview({
      graduationRequestId: graduationRequestIds[0]
    });
  };

  const handlePreviewRemoveEmail = () => {
    if (pendingRemoveRequestIds.length === 0) {
      return;
    }

    setIsRemovePreviewOpen(true);
    void loadGraduationsRemovedPreview({
      graduationRequestIds: pendingRemoveRequestIds,
      forUniversityStaff
    });
  };

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);
  const areStudentsSelected = useMemo(() => selectedCount > 0, [selectedCount]);
  const totalStudents = useMemo(() => data?.universities?.[0]?.graduationRequests?.length || 0, [data]);
  const studentsToFinalizeCount = useMemo(
    () => (areStudentsSelected ? selectedCount : totalStudents),
    [areStudentsSelected, selectedCount, totalStudents]
  );

  const activeFiltersCount = useMemo(() => {
    return (selectedPrograms.length > 0 ? 1 : 0) + (selectedPatStatus.length > 0 ? 1 : 0);
  }, [selectedPrograms, selectedPatStatus]);

  const filterState = {
    programs: selectedPrograms.length > 0,
    status: selectedPatStatus.length > 0
  };

  const handleClearAllFilters = () => {
    setSelectedPrograms([]);
    setSelectedPatStatus([]);
    clearSearch();
    deleteSearchParams(["programs[]", "status[]", "searchTerm"]);
  };

  return (
    <>
      <TableHeading
        heading="PGM Program Students Eligible for Graduation"
        body="PGA National shall review and finalize the graduation status for all eligible students associated with this PGM University Program."
        actions={
          eligibilityStats.eligible > 0 && canFinalizeGraduations
            ? [
                {
                  label: "Finalize Graduations",
                  variant: "filled" as const,
                  onClick: () => setIsGraduationDialogOpen(true),
                  testId: "open-graduation-dialog-button"
                }
              ]
            : []
        }
      />
      <Table
        data={students}
        loading={loading}
        columns={columns}
        enableRowSelection={true}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowLink={(row) => {
          const pgaId = row.original.pgaId;
          if (!universityId || !pgaId) {
            return null;
          }

          return buildUniversityStudentDetailLink({
            universityId,
            pgaId,
            returnPath: `${location.pathname}${location.search}`,
            searchParamsUrl
          });
        }}
        data-testid="eligible-students-table"
        renderExpandedRow={({ row }) => {
          const levels = row.original.programLevels || [];
          return (
            <TableDetailsPanel data={levels} columns={programColumns} title={`Program Levels (${levels.length})`} />
          );
        }}
        hasActiveFilters={searchValue || activeFiltersCount > 0}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Students Eligible for Graduation." caption="" iconName="school" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Matching Students" caption="Try adjusting your search or filters." iconName="search" />
        )}
        renderControls={(table: TableComponent<StudentRow>, { onSortClick } = {}) => {
          return (
            <>
              {error && (
                <div className="mb-4">
                  <Banner
                    variant="error"
                    description={error}
                    inline={true}
                    closable={true}
                    onClose={() => setError(null)}
                    data-testid="banner"
                  />
                </div>
              )}
              <TableControls
                useExpandedFilters={true}
                expandedFiltersState={filterState}
                onClearAllFilters={handleClearAllFilters}
                activeFilters={activeFiltersCount}
                onSortClick={onSortClick}
                bulkActions={[
                  {
                    label: "Remove from Eligible for Graduation",
                    variant: "outlined" as const,
                    onClick: () => handleRemoveFromEligible(),
                    testId: "remove-graduation-button"
                  }
                ]}
                {...bulkSelectionProps}
                onDeselectAll={deselectAll}
                totalRowCount={selectedCount}
                onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
                allRowsSelected={table.getIsAllRowsSelected()}
                someRowsSelected={table.getIsSomeRowsSelected()}
              >
                <SearchInput
                  placeholder="Search Students"
                  query={searchValue}
                  onClear={clearSearch}
                  onChange={setSearchValue}
                />
                <div data-name="programs">
                  <MultiSelect
                    label="Program / Level"
                    options={FILTER_PROGRAM_LEVEL_OPTIONS}
                    selectedValues={selectedPrograms}
                    onSelect={(value, checked) => {
                      const updatedPrograms = checked
                        ? [...selectedPrograms, value]
                        : selectedPrograms.filter((v) => v !== value);

                      setSelectedPrograms(updatedPrograms);

                      addSearchParam("programs[]", updatedPrograms.join(","));
                    }}
                    onClear={() => {
                      setSelectedPrograms([]);
                      deleteSearchParams("programs[]");
                    }}
                  />
                </div>
                <div data-name="status">
                  <MultiSelect
                    label="PAT Status"
                    options={PAT_STATUS_OPTIONS}
                    className="w-full"
                    selectedValues={selectedPatStatus}
                    onSelect={(value, checked) => {
                      const updatedStatus = checked
                        ? [...selectedPatStatus, value]
                        : selectedPatStatus.filter((v) => v !== value);

                      setSelectedPatStatus(updatedStatus);

                      addSearchParam("status[]", updatedStatus.join(","));
                    }}
                    onClear={() => {
                      setSelectedPatStatus([]);
                      deleteSearchParams("status[]");
                    }}
                  />
                </div>
              </TableControls>
            </>
          );
        }}
        renderFooter={() => <TableFooter {...bulkSelectionProps} totalRowCount={totalStudents} />}
      />

      <Menu />

      <PersonSideSheet
        open={isDetailsPanelOpen && !!selectedStudent}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={selectedStudent?.pgaId ?? null}
        title="Student Details"
        showPersonalDetails={true}
        universityName={selectedStudent?.university?.name}
        customActionButtons={
          selectedStudent
            ? [
                {
                  text: "View PGM Details",
                  variant: "outlined" as const,
                  onClick: () => {
                    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                    const separator = searchParamsUrl ? "&" : "";
                    navigate(
                      `/education/universities/${universityId}/students/${selectedStudent.pgaId}?${searchParamsUrl}${separator}returnUrl=${returnUrl}`
                    );
                  }
                },
                ...(universityStaffPermissions?.shouldShowPersonProfileActions
                  ? [
                      {
                        text: "View Full Profile",
                        variant: "text" as const,
                        onClick: () => {
                          window.open(`/people/${selectedStudent.pgaId}/person/overview`, "_blank");
                        }
                      }
                    ]
                  : [])
              ]
            : undefined
        }
      />

      {isGraduationDialogOpen && (
        <Modal
          onClosed={handleCloseGraduationDialog}
          hasFormElements={false}
          displayOverflow
          headline="Finalize Graduations"
          className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-h-[85vh]`}
          actions={
            <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
              <Button
                variant="text"
                icon="mail"
                onClick={handlePreviewFinalizeStaffEmail}
                disabled={!canPreviewFinalizeStaffEmail}
                testId="preview-finalize-staff-email-button"
              >
                Preview Staff Email
              </Button>
              {canPreviewFinalizeStudentEmail ? (
                <Button
                  variant="text"
                  icon="mail"
                  onClick={handlePreviewFinalizeStudentEmail}
                  testId="preview-finalize-student-email-button"
                >
                  Preview Student Email
                </Button>
              ) : null}
              <div className="ml-auto flex flex-wrap justify-end gap-2">
                <Button variant="text" onClick={handleCloseGraduationDialog}>
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  onClick={() => void handleMarkEligibleForGraduation()}
                  testId="finalize-graduation-button"
                >
                  Finalize {studentsToFinalizeCount} {pluralize(studentsToFinalizeCount, "Student")}
                </Button>
              </div>
            </div>
          }
        >
          <div>
            {error && (
              <div className="mb-4">
                <Banner
                  variant="error"
                  description={error}
                  inline={true}
                  closable={true}
                  onClose={() => setError(null)}
                />
              </div>
            )}
            <div className="font-label-large font-medium text-sys-brand-on-surface mb-4">
              Total Students to Finalize Graduations: {studentsToFinalizeCount}
            </div>
            <div className="text-body-medium text-sys-brand-on-surface">
              {areStudentsSelected ? (
                <>
                  Are you sure you want to mark {selectedCount} selected {pluralize(selectedCount, "student")} as
                  graduated?
                </>
              ) : (
                <>
                  Are you sure you want to mark all {totalStudents} {pluralize(totalStudents, "student")} as graduated?
                </>
              )}{" "}
              Please ensure that the {pluralize(studentsToFinalizeCount, "student")}
              {studentsToFinalizeCount === 1 ? " has" : " have"} met all program requirements. Four years of college
              credit will be added to their profiles, and their student membership bands will be ended, making them
              eligible for full membership. The {pluralize(studentsToFinalizeCount, "student")} will continue to be
              visible on the Alumni page and will be notified about next steps.
            </div>
          </div>
        </Modal>
      )}

      {isRemoveDialogOpen && (
        <Modal
          onClosed={handleCloseRemoveDialog}
          hasFormElements={false}
          displayOverflow
          headline="Remove from Eligible for Graduation"
          className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-h-[85vh]`}
          actions={
            <EmailPreviewModalActions
              onPreview={handlePreviewRemoveEmail}
              previewDisabled={pendingRemoveRequestIds.length === 0}
            >
              <Button
                variant="filled"
                onClick={() => void confirmRemoveFromEligible()}
                testId="confirm-remove-graduation-button"
              >
                Remove {pendingRemoveRequestIds.length} {pluralize(pendingRemoveRequestIds.length, "Student")}
              </Button>
            </EmailPreviewModalActions>
          }
        >
          <div>
            {error && (
              <div className="mb-4">
                <Banner
                  variant="error"
                  description={error}
                  inline={true}
                  closable={true}
                  onClose={() => setError(null)}
                  testId="remove-graduation-error-banner"
                />
              </div>
            )}
            <div className="font-label-large font-medium text-sys-brand-on-surface mb-4">
              Total Students to Remove: {pendingRemoveRequestIds.length}
            </div>
            <div className="text-body-medium text-sys-brand-on-surface">
              Are you sure you want to move {pendingRemoveRequestIds.length}{" "}
              {pluralize(pendingRemoveRequestIds.length, "student")} back to Current Students? PGA National and
              university staff will be notified.
            </div>
          </div>
        </Modal>
      )}

      <EmailPreviewDialog
        open={isFinalizeStaffPreviewOpen}
        onClosed={() => setIsFinalizeStaffPreviewOpen(false)}
        headline="Staff Finalized Email Preview"
        subject={graduationsFinalizedPreviewLoading ? undefined : graduationsFinalizedPreview?.subject}
        html={graduationsFinalizedPreviewLoading ? undefined : graduationsFinalizedPreview?.html}
        loading={graduationsFinalizedPreviewLoading}
        errorMessage={graduationsFinalizedPreviewLoading ? null : (graduationsFinalizedPreviewError?.message ?? null)}
      />
      <EmailPreviewDialog
        open={isFinalizeStudentPreviewOpen}
        onClosed={() => setIsFinalizeStudentPreviewOpen(false)}
        headline="Student Congratulations Email Preview"
        subject={graduationCongratulationsPreviewLoading ? undefined : graduationCongratulationsPreview?.subject}
        html={graduationCongratulationsPreviewLoading ? undefined : graduationCongratulationsPreview?.html}
        loading={graduationCongratulationsPreviewLoading}
        errorMessage={
          graduationCongratulationsPreviewLoading ? null : (graduationCongratulationsPreviewError?.message ?? null)
        }
      />
      <EmailPreviewDialog
        open={isRemovePreviewOpen}
        onClosed={() => setIsRemovePreviewOpen(false)}
        headline="Removed from Eligible Email Preview"
        subject={graduationsRemovedPreviewLoading ? undefined : graduationsRemovedPreview?.subject}
        html={graduationsRemovedPreviewLoading ? undefined : graduationsRemovedPreview?.html}
        loading={graduationsRemovedPreviewLoading}
        errorMessage={graduationsRemovedPreviewLoading ? null : (graduationsRemovedPreviewError?.message ?? null)}
      />
    </>
  );
}
